import React, { ReactNode, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  ImportContacts,
  AssignmentInd,
  ExitToApp,
  Person,
  PostAdd,
  AttachMoney,
  InfoOutlined,
  VpnKey,
  EmojiTransportation,
  AddCircleOutline,
  Map,
  Business,
  Assessment,
  Notifications,
  History,
  SupervisorAccount,
  ExpandLess,
  ExpandMore,
} from '@material-ui/icons';
import { List, ListItem, ListItemText, ListItemIcon, Tooltip, Collapse } from '@material-ui/core';

import history from '~/services/history';

import { logout } from '~/store/ducks/auth';

import { Link, Button } from './styles';
import { CustomRoles } from '~/types';

export interface MenuProps {
  toggleDrawer?: (option: boolean) => void;
  menuIsOpen: boolean;
}

interface ItemProps extends Omit<MenuItemProps, 'handleLink' | 'menuIsOpen'> {
  roles?: CustomRoles[];
}

export function Menu({ toggleDrawer, menuIsOpen }: MenuProps) {
  const dispatch = useDispatch();
  const { 'custom:role': role } = useSelector(state => state.auth.user);

  const [checklistOpen, setChecklistOpen] = useState(false);
  const [gestaoOpen, setGestaoOpen] = useState(false);

  function handleLink(url: string) {
    history.push(url);
    if (typeof toggleDrawer === 'function') {
      toggleDrawer(false);
    }
  }

  function logoutApp() {
    dispatch(logout());
  }

  function Item({ roles = [CustomRoles.BACKOFFICE], ...rest }: ItemProps) {
    if (!roles?.includes(role)) {
      return null;
    }

    return <MenuItem handleLink={handleLink} menuIsOpen={menuIsOpen} {...rest} />;
  }

  const checklistAllowed = [CustomRoles.BACKOFFICE].includes(role);
  const gestaoAllowed = [CustomRoles.BACKOFFICE, CustomRoles.B2B].includes(role);

  return (
    <>
      <List>
        <Item
          to='/dashboard'
          text='Dashboard'
          icon={<Assessment />}
          roles={[CustomRoles.BACKOFFICE, CustomRoles.B2B]}
        />

        <Item
          to='/new-opportunity'
          text='Oportunidade'
          icon={<AddCircleOutline />}
          roles={[CustomRoles.BACKOFFICE, CustomRoles.B2B]}
        />

        <Item
          to='/admin-opportunity'
          text='Gerenciar oportunidades'
          icon={<ImportContacts />}
        />

        <Item to='/import' text='Importação' icon={<SupervisorAccount />} />

        <Item to='/table-prices' text='Tabela Empresa' icon={<AttachMoney />} />

        <Item to='/contractor-price-table' text='Tabela Prestador' icon={<AttachMoney />} />

        <Item to='/coverage-plans' text='Cobertura' icon={<PostAdd />} />

        {checklistAllowed && (
          <>
            <ListItem button onClick={() => setChecklistOpen(prev => !prev)} style={{ paddingLeft: 26 }}>
              <Tooltip title='Checklist' placement='right' disableHoverListener={menuIsOpen}>
                <ListItemIcon>
                  <InfoOutlined />
                </ListItemIcon>
              </Tooltip>
              <ListItemText>Checklist</ListItemText>
              {menuIsOpen && (checklistOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItem>
            <Collapse in={checklistOpen} timeout='auto' unmountOnExit>
              <List component='div' disablePadding>
                <Item
                  to='/additional-information'
                  text='Checklist de Atendimento'
                  icon={<InfoOutlined />}
                  nested
                />
                <Item to='/system-history' text='Checklist Histórico' icon={<History />} nested />
              </List>
            </Collapse>
          </>
        )}

        {gestaoAllowed && (
          <>
            <ListItem button onClick={() => setGestaoOpen(prev => !prev)} style={{ paddingLeft: 26 }}>
              <Tooltip title='Gestão' placement='right' disableHoverListener={menuIsOpen}>
                <ListItemIcon>
                  <SupervisorAccount />
                </ListItemIcon>
              </Tooltip>
              <ListItemText>Gestão</ListItemText>
              {menuIsOpen && (gestaoOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItem>
            <Collapse in={gestaoOpen} timeout='auto' unmountOnExit>
              <List component='div' disablePadding>
                <Item to='/contractor-manager' text='Gestão de Prestadores' icon={<Person />} nested />

                <Item to='/b2b-manager' text='Gestão B2B' icon={<Business />} nested />

                <Item
                  to='/opportunity-manager'
                  text='Gestão de Oportunidades'
                  icon={<EmojiTransportation />}
                  roles={[CustomRoles.BACKOFFICE, CustomRoles.B2B]}
                  nested
                />

                <Item
                  to='/map-opportunities?refresh_interval_ms=40000'
                  text='Mapa de Oportunidades'
                  icon={<Map />}
                  roles={[CustomRoles.BACKOFFICE, CustomRoles.B2B]}
                  nested
                />

                <Item
                  to='/simovsat?refresh_interval_ms=40000'
                  text='Gestão Simovsat'
                  icon={<EmojiTransportation />}
                  roles={[CustomRoles.BACKOFFICE, CustomRoles.B2B]}
                  nested
                />

                <Item
                  to='/hinova?refresh_interval_ms=40000'
                  text='Gestão Hinova'
                  icon={<EmojiTransportation />}
                  roles={[CustomRoles.BACKOFFICE, CustomRoles.B2B]}
                  nested
                />

                <Item to='/association-manager' text='Gestão de Empresas' icon={<AssignmentInd />} nested />

                <Item
                  to='/customer-manager'
                  text='Gestão de Clientes'
                  icon={<Person />}
                  roles={[CustomRoles.BACKOFFICE, CustomRoles.B2B]}
                  nested
                />
              </List>
            </Collapse>
          </>
        )}

        <Item to='/notifications' text='Notificações' icon={<Notifications />} />
      </List>

      <div style={{ flex: 1 }} />

      <List style={{ paddingBottom: 30 }}>
        <Item to='/change-password' text='Alterar Senha' icon={<VpnKey />} />

        <ListItem>
          <Button onClick={() => logoutApp()}>
            <ListItemIcon>
              <ExitToApp />
            </ListItemIcon>
            <ListItemText>Sair</ListItemText>
          </Button>
        </ListItem>
      </List>
    </>
  );
}

export default Menu;

interface MenuItemProps {
  to: string;
  handleLink: (url: string) => void;
  text: string;
  icon: ReactNode;
  menuIsOpen: boolean;
  nested?: boolean;
}

function MenuItem(props: MenuItemProps) {
  return (
    <ListItem style={{ padding: 0, paddingLeft: props.nested ? 16 : 0 }}>
      <Link to={props.to} onClick={() => props.handleLink(props.to)}>
        <Tooltip title={props.text} placement='right' disableHoverListener={props.menuIsOpen}>
          <ListItemIcon>{props.icon}</ListItemIcon>
        </Tooltip>

        <ListItemText>{props.text}</ListItemText>
      </Link>
    </ListItem>
  );
}
